/**
 * Compiled CSS for AssistantChat.
 *
 * Source of truth:
 *   clientapp/app/assets/scss/shared/landing-screens/_assistant_chat.scss
 */
export const ASSISTANT_CHAT_CSS = `@charset "UTF-8";
/* ==========================================================================
   AssistantChat — modern, minimalist chat UI shared by every assistant
   ========================================================================== */
.assistant-chat {
  /* Theme tokens — overridden by .dark-mode below.
     These match the legacy chat palette ($content-bg / $card-bg / theme primary)
     so the new UI feels consistent with the rest of the app and provides good
     contrast for long-form research output (charts, tables, prose). */
  --ac-bg: #ffffff; /* chat-panel canvas (light) — was $card-bg */
  --ac-surface: #ffffff; /* composer surface */
  --ac-surface-2: #f7f5f9; /* code blocks / hover tints inside the panel */
  --ac-surface-hover: #eee8f1;
  --ac-sidebar-bg: #faf6fc; /* tinted column distinct from white main pane */
  --ac-border: #ebe4ee; /* matches legacy $border-color tone */
  --ac-border-strong: #d6cdd9;
  --ac-text: #14161b;
  --ac-text-muted: #6b7280;
  --ac-text-faint: #9aa0aa;
  --ac-accent: #b66dff; /* matches legacy theme-color(primary) */
  --ac-accent-soft: rgba(182, 109, 255, 0.10);
  --ac-accent-text: #ffffff;
  /* Message-card colors — assistant card uses the legacy $content-bg (lavender)
     so it reads as a soft "answer card" sitting inside the white chat panel. */
  --ac-msg-assistant-bg: #f2edf3;
  --ac-msg-assistant-border: transparent;
  --ac-msg-user-bg: #b66dff;
  --ac-msg-user-text: #ffffff;
  --ac-success: #34c759;
  --ac-warning: #f59e0b;
  --ac-danger: #ef4444;
  --ac-info: #1e88e5;
  --ac-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --ac-shadow-md: 0 4px 16px rgba(15, 23, 42, 0.06);
  --ac-shadow-lg: 0 12px 40px rgba(15, 23, 42, 0.10);
  --ac-radius-sm: 8px;
  --ac-radius-md: 14px;
  --ac-radius-lg: 20px;
  --ac-radius-pill: 999px;
  /* Primary reading column — centered; ~72% of main-pane width (capped) so
     messages + composer align with the tighter reading band in the layout spec.
     Override with \`--ac-content-max\` on \`.assistant-chat\` if a host needs a different width. */
  --ac-content-max: min(720px, 72%);
  position: relative;
  display: flex;
  flex-direction: row;
  width: 100%;
  /* Default: take all the vertical space the parent gives us. The host page
     wrapper (.assistant-chat-page) is flex-column and grows to fill the
     content-wrapper, so the chat ends up filling the viewport below the nav. */
  /* \`flex-basis: 0\` so this row flex item fills the height given by the parent
     column (\`.assistant-chat-page\`), instead of sizing to content intrinsic
     height — which would push the composer past \`overflow: hidden\` edges. */
  flex: 1 1 0;
  min-height: 0;
  background: var(--ac-bg);
  color: var(--ac-text);
  overflow: hidden;
  font-feature-settings: "ss01", "cv11";
}
.assistant-chat *, .assistant-chat *::before, .assistant-chat *::after {
  box-sizing: border-box;
}
.assistant-chat button {
  font-family: inherit;
}
.assistant-chat {
  /* ---------------- Sidebar ---------------- */
}
.assistant-chat .ac-sidebar {
  flex: 0 0 280px;
  width: 280px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid var(--ac-border);
  /* Distinct shade from the main pane:
       light mode → soft lavender tint sets the sidebar apart from white
       dark mode  → lifted #222328 above the chat-canvas #1a191d
     (\`--ac-sidebar-bg\` is dedicated so we don't conflict with code-block
      and inline tints which use --ac-surface-2.) */
  background: var(--ac-sidebar-bg);
  transition: transform 0.25s ease;
}
.assistant-chat .ac-sidebar-header {
  padding: 16px 18px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: 1px solid var(--ac-border);
}
.assistant-chat .ac-sidebar-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.assistant-chat .ac-sidebar-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--ac-text);
  margin: 0;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.assistant-chat .ac-sidebar-subtitle {
  font-size: 0.78rem;
  color: var(--ac-text-muted);
  margin: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.assistant-chat .ac-sidebar-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.assistant-chat .ac-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--ac-radius-sm);
  border: none;
  background: transparent;
  color: var(--ac-text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
  padding: 0;
}
.assistant-chat .ac-icon-btn:hover {
  background: var(--ac-surface-hover);
  color: var(--ac-text);
}
.assistant-chat .ac-icon-btn:active {
  transform: scale(0.96);
}
.assistant-chat .ac-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.assistant-chat .ac-icon-btn .MuiSvgIcon-root {
  font-size: 18px;
}
.assistant-chat .ac-new-chat-btn {
  margin-top: 4px;
  width: 100%;
  height: 36px;
  border-radius: var(--ac-radius-pill);
  border: 1px solid var(--ac-border-strong);
  background: var(--ac-surface);
  color: var(--ac-text);
  font-weight: 500;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.assistant-chat .ac-new-chat-btn:hover {
  background: var(--ac-surface-hover);
  border-color: var(--ac-text-faint);
}
.assistant-chat {
  /* Sidebar context block (price, industry, filing links etc.) */
}
.assistant-chat .ac-sidebar-context {
  padding: 12px 18px;
  border-bottom: 1px solid var(--ac-border);
  font-size: 0.8rem;
  color: var(--ac-text-muted);
  line-height: 1.5;
}
.assistant-chat .ac-sidebar-context p {
  margin: 0 0 4px;
}
.assistant-chat .ac-sidebar-context p:last-child {
  margin-bottom: 0;
}
.assistant-chat .ac-sidebar-context a {
  color: var(--ac-accent);
  text-decoration: none;
}
.assistant-chat .ac-sidebar-context a:hover {
  text-decoration: underline;
}
.assistant-chat .ac-sidebar-context strong {
  color: var(--ac-text);
  font-weight: 500;
}
.assistant-chat .ac-thread-search-wrap {
  flex: 0 0 auto;
  padding: 10px 12px 8px;
  border-bottom: 1px solid var(--ac-border);
}
.assistant-chat .ac-thread-search-field .MuiOutlinedInput-root {
  border-radius: var(--ac-radius-sm);
  font-size: 0.82rem;
  background: var(--ac-surface);
}
.assistant-chat .ac-thread-search-field .MuiOutlinedInput-root fieldset {
  border-color: var(--ac-border);
}
.assistant-chat .ac-thread-search-field .MuiOutlinedInput-root:hover fieldset {
  border-color: var(--ac-border-strong);
}
.assistant-chat .ac-thread-search-field .MuiOutlinedInput-root.Mui-focused fieldset {
  border-color: var(--ac-accent);
}
.assistant-chat .ac-thread-search-field .MuiInputBase-input {
  padding: 8px 4px 8px 0;
  color: var(--ac-text);
}
.assistant-chat .ac-thread-search-field .MuiInputBase-input::placeholder {
  color: var(--ac-text-faint);
  opacity: 1;
}
.assistant-chat .ac-thread-more-btn {
  flex: 0 0 auto;
  margin: 8px 12px 4px;
  padding: 8px 12px;
  align-self: stretch;
  text-align: center;
  border: 1px dashed var(--ac-border-strong);
  border-radius: var(--ac-radius-sm);
  background: transparent;
  color: var(--ac-accent);
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.assistant-chat .ac-thread-more-btn:hover {
  background: var(--ac-accent-soft);
  border-color: var(--ac-accent);
}
.assistant-chat .ac-thread-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 10px 30px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.assistant-chat .ac-thread-empty-filter {
  margin: 12px 10px;
  font-size: 0.8rem;
  color: var(--ac-text-muted);
  text-align: center;
  line-height: 1.4;
}
.assistant-chat .ac-thread {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 8px;
  padding: 10px 12px;
  border-radius: var(--ac-radius-sm);
  cursor: pointer;
  transition: background 0.15s ease;
  position: relative;
}
.assistant-chat .ac-thread:hover {
  background: var(--ac-surface-hover);
}
.assistant-chat .ac-thread:hover .ac-thread-delete {
  opacity: 1;
}
.assistant-chat .ac-thread-header {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--ac-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  grid-column: 1/-1;
}
.assistant-chat .ac-thread-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  color: var(--ac-text-faint);
  grid-column: 1;
}
.assistant-chat .ac-thread-agent-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px;
  border-radius: var(--ac-radius-pill);
  background: var(--ac-surface-2);
  color: var(--ac-text-muted);
  font-size: 0.68rem;
  font-weight: 500;
}
.assistant-chat .ac-thread-agent-chip::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--chip-color, var(--ac-text-faint));
}
.assistant-chat .ac-thread-delete {
  grid-column: 2;
  align-self: center;
  opacity: 0;
  transition: opacity 0.15s ease;
}
@media (hover: none) {
  .assistant-chat .ac-thread-delete {
    opacity: 1;
  }
}
.assistant-chat {
  /* ---------------- Mobile sidebar overlay ---------------- */
}
.assistant-chat .ac-sidebar-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 40;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.assistant-chat.ac-sidebar-open .ac-sidebar-backdrop {
  opacity: 1;
  pointer-events: auto;
}
.assistant-chat {
  /* ---------------- Main pane ---------------- */
}
.assistant-chat .ac-main {
  flex: 1 1 auto;
  min-width: 0;
  /* \`min-height: 0\` is essential — without it, the column flex item refuses
     to shrink below its content's intrinsic height, which forces the
     composer below the visible area when there are many messages. */
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--ac-bg);
  position: relative;
}
.assistant-chat .ac-main-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-bottom: 1px solid var(--ac-border);
  background: var(--ac-bg);
  z-index: 5;
  flex: 0 0 auto;
}
.assistant-chat .ac-burger {
  display: none;
}
.assistant-chat .ac-main-header-info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.assistant-chat .ac-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
  border: 1px solid var(--ac-border);
  background: var(--ac-surface-2);
}
.assistant-chat .ac-avatar-monogram {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.8rem;
  color: #fff;
  background: var(--ac-accent);
}
.assistant-chat .ac-header-text {
  min-width: 0;
  line-height: 1.2;
}
.assistant-chat .ac-header-name {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--ac-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.assistant-chat .ac-header-context {
  font-size: 0.78rem;
  color: var(--ac-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.assistant-chat {
  /* ---------------- Conversation ---------------- */
}
.assistant-chat .ac-conversation {
  flex: 1 1 auto;
  /* \`min-height: 0\` is required so the conversation can shrink below its
     content's intrinsic height and let \`overflow-y: auto\` actually scroll
     within the bounded chat area instead of pushing siblings (composer)
     past the viewport edge. */
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 28px 28px 20px;
  scroll-behavior: smooth;
  /* Let scroll-to-end and keyboard focus leave comfortable space above the docked composer. */
  scroll-padding-bottom: 32px;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
}
.assistant-chat .ac-conversation-inner {
  width: 100%;
  max-width: var(--ac-content-max, min(720px, 72%));
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  gap: 22px;
  /* Extra room past the last bubble so the answer doesn't feel cut off and
     users can scroll the full depth of long reports (old page-scroll feel). */
  padding-bottom: 40px;
}
.assistant-chat {
  /* Empty state — centered hero */
}
.assistant-chat .ac-empty-state {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 16px;
  gap: 14px;
  min-height: 320px;
}
.assistant-chat .ac-empty-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--ac-accent);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 600;
  overflow: hidden;
  box-shadow: var(--ac-shadow-md);
}
.assistant-chat .ac-empty-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.assistant-chat .ac-empty-name {
  font-size: 1.35rem;
  font-weight: 600;
  color: var(--ac-text);
  margin: 0;
}
.assistant-chat .ac-empty-context {
  font-size: 0.95rem;
  color: var(--ac-text-muted);
  max-width: 460px;
  margin: 0;
  line-height: 1.5;
}
.assistant-chat {
  /* Messages */
}
.assistant-chat .ac-msg-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
}
.assistant-chat .ac-msg-row.user {
  justify-content: flex-end;
}
.assistant-chat .ac-msg-row.assistant {
  justify-content: flex-start;
}
.assistant-chat {
  /* Avatar slot is hidden by default to maximize horizontal space for
     content; assistants that want avatars can re-enable per-host. */
}
.assistant-chat .ac-msg-avatar-slot {
  display: none;
}
.assistant-chat .ac-msg-avatar-slot .ac-avatar,
.assistant-chat .ac-msg-avatar-slot .ac-avatar-monogram {
  width: 28px;
  height: 28px;
  font-size: 0.75rem;
}
.assistant-chat .ac-msg {
  border-radius: var(--ac-radius-md);
  word-wrap: break-word;
  overflow-wrap: anywhere;
}
.assistant-chat {
  /* Outgoing user message — solid accent bubble, right-aligned, capped width
     so it reads as a chat bubble without dominating the column. */
}
.assistant-chat .ac-msg.user {
  background: var(--ac-msg-user-bg);
  color: var(--ac-msg-user-text);
  padding: 14px 18px;
  border: 1px solid transparent;
  max-width: min(560px, 85%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.assistant-chat .ac-msg.user .ac-msg-content {
  color: var(--ac-msg-user-text);
}
.assistant-chat .ac-msg.user .ac-msg-content a {
  color: var(--ac-msg-user-text);
  text-decoration: underline;
}
.assistant-chat {
  /* Frontend-only FIFO queue. Queued prompts stay visually related to outgoing
     messages, but use a subdued treatment until they actually start. */
}
.assistant-chat .ac-queue {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.assistant-chat .ac-queue-status {
  min-height: 28px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-right: 4px;
  color: var(--ac-text-muted);
  font-size: 0.78rem;
  font-weight: 600;
}
.assistant-chat .ac-queue.paused .ac-queue-status {
  color: var(--ac-warning);
}
.assistant-chat .ac-queue-resume {
  border: 1px solid var(--ac-border-strong);
  border-radius: var(--ac-radius-pill);
  padding: 4px 10px;
  background: var(--ac-surface);
  color: var(--ac-text);
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.assistant-chat .ac-queue-resume:hover {
  border-color: var(--ac-accent);
  background: var(--ac-accent-soft);
}
.assistant-chat .ac-queue-resume:focus-visible {
  outline: 2px solid var(--ac-accent);
  outline-offset: 2px;
}
.assistant-chat .ac-queue-messages {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.assistant-chat .ac-msg.user.ac-msg-queued {
  background: var(--ac-accent-soft);
  color: var(--ac-text);
  border: 1px dashed var(--ac-border-strong);
  box-shadow: none;
}
.assistant-chat .ac-msg.user.ac-msg-queued .ac-msg-content,
.assistant-chat .ac-msg.user.ac-msg-queued .ac-msg-content a {
  color: var(--ac-text);
}
.assistant-chat .ac-queued-msg-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 9px;
  padding-top: 7px;
  border-top: 1px solid var(--ac-border);
  color: var(--ac-text-muted);
  font-size: 0.74rem;
  font-weight: 600;
}
.assistant-chat .ac-queued-msg-position {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.assistant-chat .ac-queued-msg-remove {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  padding: 0;
  background: transparent;
  color: var(--ac-text-muted);
  font-size: 1rem;
  cursor: pointer;
}
.assistant-chat .ac-queued-msg-remove:hover {
  background: var(--ac-surface-hover);
  color: var(--ac-danger);
}
.assistant-chat .ac-queued-msg-remove:focus-visible {
  outline: 2px solid var(--ac-accent);
  outline-offset: 2px;
}
.assistant-chat {
  /* Incoming assistant message — full-width content card so long-form
     research output (charts, tables, headings, lists) has room to render.
     Mirrors legacy \`.incoming-chat .chat-message { background: $content-bg; }\` */
}
.assistant-chat .ac-msg.assistant {
  background: var(--ac-msg-assistant-bg);
  border: 1px solid var(--ac-msg-assistant-border);
  padding: 22px 26px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}
.assistant-chat .ac-msg-content {
  font-size: 0.95rem;
  line-height: 1.6;
}
.assistant-chat .ac-msg-content p {
  margin: 0 0 10px;
}
.assistant-chat .ac-msg-content p:last-child {
  margin-bottom: 0;
}
.assistant-chat .ac-msg-content ul, .assistant-chat .ac-msg-content ol {
  padding-left: 22px;
  margin: 0 0 10px;
}
.assistant-chat .ac-msg-content code:not(pre code) {
  background: var(--ac-surface-2);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.85em;
}
.assistant-chat .ac-msg-content pre {
  background: var(--ac-surface-2);
  padding: 12px 14px;
  border-radius: var(--ac-radius-sm);
  overflow-x: auto;
  margin: 10px 0;
}
.assistant-chat .ac-msg-content blockquote {
  border-left: 3px solid var(--ac-border-strong);
  margin: 10px 0;
  padding: 4px 12px;
  color: var(--ac-text-muted);
}
.assistant-chat {
  /* Tables — research output (financials, comparisons, etc.) renders cleanly
     in either theme; outer wrapper allows horizontal scroll on overflow. */
}
.assistant-chat .ac-msg-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
  font-size: 0.88rem;
  background: var(--ac-bg);
  border: 1px solid var(--ac-border);
  border-radius: var(--ac-radius-sm);
  overflow: hidden;
  display: table;
}
.assistant-chat .ac-msg-content thead {
  background: var(--ac-surface-2);
}
.assistant-chat .ac-msg-content th,
.assistant-chat .ac-msg-content td {
  padding: 4px 8px;
  line-height: 1.35;
  text-align: left;
  border: 1px solid var(--ac-border);
  vertical-align: middle;
}
.assistant-chat .ac-msg-content th {
  font-weight: 600;
  color: var(--ac-text);
  white-space: nowrap;
}
.assistant-chat .ac-msg-content td:first-child {
  white-space: nowrap;
}
.assistant-chat {
  /* Headings inside an assistant card — slightly tighter to feel embedded */
}
.assistant-chat .ac-msg-content h1 {
  font-size: 1.5rem;
  margin: 14px 0 10px;
}
.assistant-chat .ac-msg-content h2 {
  font-size: 1.25rem;
  margin: 14px 0 8px;
}
.assistant-chat .ac-msg-content h3 {
  font-size: 1.08rem;
  margin: 12px 0 6px;
}
.assistant-chat .ac-msg-content h1:first-child,
.assistant-chat .ac-msg-content h2:first-child,
.assistant-chat .ac-msg-content h3:first-child {
  margin-top: 0;
}
.assistant-chat .ac-msg-content hr {
  border: 0;
  border-top: 1px solid var(--ac-border);
  margin: 16px 0;
}
.assistant-chat .ac-msg-content img {
  max-width: 100%;
  height: auto;
  border-radius: var(--ac-radius-sm);
  margin: 10px 0;
}
.assistant-chat .ac-msg-actions {
  margin-top: 6px;
  display: flex;
  gap: 2px;
  opacity: 1;
}
.assistant-chat {
  /* Typing indicator */
}
.assistant-chat .ac-typing {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 12px 14px;
  background: var(--ac-surface-2);
  border-radius: var(--ac-radius-md);
}
.assistant-chat .ac-typing span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ac-text-faint);
  animation: ac-typing-bounce 1.2s ease-in-out infinite;
}
.assistant-chat .ac-typing span:nth-child(2) {
  animation-delay: 0.15s;
}
.assistant-chat .ac-typing span:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes ac-typing-bounce {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}
.assistant-chat .ac-visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
.assistant-chat .ac-thinking-progress {
  --ac-thinking-accent: var(--ac-accent);
  position: relative;
  overflow: hidden;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--ac-border);
  border-radius: var(--ac-radius-md);
  background: var(--ac-surface-2);
  box-shadow: var(--ac-shadow-sm);
}
.assistant-chat .ac-thinking-progress::before {
  content: "";
  position: absolute;
  top: 0;
  left: -40%;
  width: 40%;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--ac-thinking-accent), transparent);
  animation: ac-thinking-sweep 2.4s ease-in-out infinite;
}
.assistant-chat .ac-thinking-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.assistant-chat .ac-thinking-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: var(--ac-text);
  font-size: 0.88rem;
  font-weight: 600;
}
.assistant-chat .ac-thinking-status-dot {
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--ac-thinking-accent);
  animation: ac-thinking-pulse 1.6s ease-in-out infinite;
}
.assistant-chat .ac-thinking-elapsed {
  flex: 0 0 auto;
  min-width: 30px;
  color: var(--ac-text-muted);
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.assistant-chat .ac-thinking-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}
.assistant-chat .ac-thinking-step {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  align-items: center;
  gap: 9px;
  min-height: 22px;
  color: var(--ac-text-muted);
  font-size: 0.82rem;
  line-height: 1.4;
  transition: color 0.2s ease, opacity 0.2s ease;
}
.assistant-chat .ac-thinking-step-marker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}
.assistant-chat .ac-thinking-step-marker .MuiSvgIcon-root {
  color: var(--ac-thinking-accent);
  font-size: 18px;
}
.assistant-chat .ac-thinking-step-dot {
  width: 8px;
  height: 8px;
  border: 1px solid var(--ac-border-strong);
  border-radius: 50%;
  background: var(--ac-surface);
}
.assistant-chat .ac-thinking-step.pending {
  opacity: 0.48;
}
.assistant-chat .ac-thinking-step.active {
  color: var(--ac-text);
  font-weight: 500;
}
.assistant-chat .ac-thinking-step.active .ac-thinking-step-dot {
  border-color: var(--ac-thinking-accent);
  background: var(--ac-thinking-accent);
  animation: ac-thinking-step-pulse 1.25s ease-in-out infinite;
}
.assistant-chat .ac-thinking-step.completed {
  color: var(--ac-text-muted);
}
.assistant-chat .ac-thinking-step.real-event {
  align-items: start;
  animation: ac-thinking-event-arrive 0.28s ease-out;
}
.assistant-chat .ac-thinking-step.real-event.current {
  color: var(--ac-text);
}
.assistant-chat .ac-thinking-step.real-event > span:last-child {
  min-width: 0;
  overflow-wrap: anywhere;
}
.assistant-chat .ac-thinking-step.live-heartbeat {
  margin-top: 2px;
  padding-top: 9px;
  border-top: 1px solid var(--ac-border);
}
.assistant-chat .ac-thinking-step-elapsed {
  color: var(--ac-text-muted);
  font-size: 0.76rem;
  font-variant-numeric: tabular-nums;
  font-weight: 400;
}
.assistant-chat .ac-thinking-long-wait {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--ac-border);
  color: var(--ac-text-muted);
  font-size: 0.78rem;
  line-height: 1.45;
}
@keyframes ac-thinking-sweep {
  0% { transform: translateX(0); opacity: 0; }
  20% { opacity: 0.85; }
  80% { opacity: 0.85; }
  100% { transform: translateX(350%); opacity: 0; }
}
@keyframes ac-thinking-pulse {
  0%, 100% { opacity: 0.55; box-shadow: 0 0 0 0 var(--ac-thinking-accent); }
  50% { opacity: 1; box-shadow: 0 0 0 5px transparent; }
}
@keyframes ac-thinking-step-pulse {
  0%, 100% { transform: scale(0.8); opacity: 0.55; }
  50% { transform: scale(1.15); opacity: 1; }
}
@keyframes ac-thinking-event-arrive {
  from { transform: translateY(4px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .assistant-chat .ac-thinking-progress::before,
  .assistant-chat .ac-thinking-status-dot,
  .assistant-chat .ac-thinking-step.active .ac-thinking-step-dot,
  .assistant-chat .ac-thinking-step.real-event {
    animation: none;
  }
}
.assistant-chat {
  /* ---------------- Notice cards (login / quota / error / throttled) ---------------- */
}
.assistant-chat .ac-notice {
  border: 1px solid var(--ac-border);
  background: var(--ac-surface);
  border-radius: var(--ac-radius-md);
  padding: 18px 20px;
  text-align: center;
  max-width: 480px;
  margin: 8px auto;
  box-shadow: var(--ac-shadow-sm);
}
.assistant-chat .ac-notice-icon {
  font-size: 32px;
  line-height: 1;
  margin-bottom: 8px;
  color: var(--ac-text-muted);
}
.assistant-chat .ac-notice.warn .ac-notice-icon {
  color: var(--ac-warning);
}
.assistant-chat .ac-notice.danger .ac-notice-icon {
  color: var(--ac-danger);
}
.assistant-chat .ac-notice.info .ac-notice-icon {
  color: var(--ac-info);
}
.assistant-chat .ac-notice-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--ac-text);
  margin: 0 0 6px;
}
.assistant-chat .ac-notice-message {
  font-size: 0.9rem;
  color: var(--ac-text-muted);
  margin: 0 0 12px;
  line-height: 1.5;
}
.assistant-chat .ac-notice-actions {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.assistant-chat .ac-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  background: var(--ac-accent);
  color: var(--ac-accent-text);
  border-radius: var(--ac-radius-pill);
  font-weight: 500;
  font-size: 0.88rem;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.15s ease;
}
.assistant-chat .ac-btn-primary:hover {
  filter: brightness(1.05);
  color: var(--ac-accent-text);
  text-decoration: none;
}
.assistant-chat .ac-btn-primary:active {
  transform: scale(0.98);
}
.assistant-chat .ac-link-muted {
  color: var(--ac-text-muted);
  text-decoration: none;
  font-size: 0.82rem;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
}
.assistant-chat .ac-link-muted:hover {
  color: var(--ac-text);
  text-decoration: underline;
}
.assistant-chat {
  /* ---------------- Composer ---------------- */
}
.assistant-chat .ac-composer-wrap {
  flex: 0 0 auto;
  flex-shrink: 0;
  z-index: 12;
  /* Match \`.ac-conversation\` horizontal padding so the composer lines up with the thread column. */
  padding: 10px 28px calc(10px + env(safe-area-inset-bottom, 0px));
  background: var(--ac-bg);
  /* Docked composer: stays in the flex column below the scroll region so it
     remains visible while the thread scrolls (same idea as a sticky footer bar). */
  border-top: 1px solid var(--ac-border);
  box-shadow: 0 -6px 24px rgba(15, 23, 42, 0.06);
}
.assistant-chat .ac-composer-inner {
  max-width: var(--ac-content-max, min(720px, 72%));
  margin-left: auto;
  margin-right: auto;
  width: 100%;
}
.assistant-chat .ac-composer {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 6px 8px 6px 8px;
  border-radius: var(--ac-radius-lg);
  background: var(--ac-surface);
  border: 1px solid var(--ac-border);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.assistant-chat .ac-composer:focus-within {
  border-color: var(--ac-accent);
  box-shadow: 0 0 0 3px var(--ac-accent-soft);
}
.assistant-chat .ac-composer-examples {
  flex: 0 0 auto;
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1px;
  padding: 6px;
  min-width: 36px;
  min-height: 36px;
  border: none;
  border-radius: var(--ac-radius-sm);
  background: transparent;
  color: var(--ac-text-muted);
  font-family: inherit;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}
.assistant-chat .ac-composer-examples:hover {
  color: var(--ac-accent);
  background: var(--ac-surface-hover);
}
.assistant-chat .ac-composer-examples .MuiSvgIcon-root {
  font-size: 20px;
  flex-shrink: 0;
  opacity: 0.9;
}
.assistant-chat .ac-composer-textarea {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  color: var(--ac-text);
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.5;
  padding: 8px 4px;
  max-height: 200px;
  overflow-y: auto;
}
.assistant-chat .ac-composer-textarea::placeholder {
  color: var(--ac-text-faint);
}
.assistant-chat .ac-composer-send {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--ac-accent);
  color: var(--ac-accent-text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
}
.assistant-chat .ac-composer-send .MuiSvgIcon-root {
  font-size: 18px;
}
.assistant-chat .ac-composer-send:disabled {
  background: var(--ac-surface-hover);
  color: var(--ac-text-faint);
  cursor: not-allowed;
}
.assistant-chat .ac-composer-send:not(:disabled):hover {
  filter: brightness(1.05);
}
.assistant-chat .ac-composer-send:active {
  transform: scale(0.95);
}
.assistant-chat {
  /* ---------------- Responsive ---------------- */
}
@media (max-width: 991px) {
  .assistant-chat .ac-sidebar {
    position: absolute;
    inset: 0 auto 0 0;
    width: min(320px, 88vw);
    z-index: 50;
    transform: translateX(-105%);
    box-shadow: var(--ac-shadow-lg);
  }
  .assistant-chat.ac-sidebar-open .ac-sidebar {
    transform: translateX(0);
  }
  .assistant-chat .ac-burger {
    display: inline-flex;
  }
}
@media (max-width: 600px) {
  .assistant-chat .ac-conversation {
    padding: 18px 12px 16px;
  }
  .assistant-chat .ac-composer-wrap {
    padding-left: 12px;
    padding-right: 12px;
  }
  .assistant-chat .ac-msg.user {
    max-width: 92%;
  }
  .assistant-chat .ac-empty-name {
    font-size: 1.2rem;
  }
}

/* ==========================================================================
   Host-page wrapper — flex column that owns the breadcrumb + chat stack and
   fills whatever vertical space the parent layout container provides.
   ========================================================================== */
.assistant-chat-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  /* Min-height fallback when the wrapper happens to live in a non-flex parent. */
  min-height: 0;
}

.assistant-chat-breadcrumb {
  flex: 0 0 auto;
  margin-bottom: 12px;
}

/* ==========================================================================
   Fullscreen mode — opted in by <AssistantChat /> via \`body.assistant-chat-active\`.
   Collapses the standard layout's content-wrapper padding and removes the
   page footer so the chat fills the entire viewport below the fixed navbar.
   The 70px offset matches \`.page-body-wrapper { min-height: calc(100vh - 70px) }\`
   from the base style.css.
   ========================================================================== */
/* Page-bg tokens for the area AROUND the chat panel — these match the
   standard app palette ($content-bg #f2edf3 light / #151519 dark) so the
   chat page reads consistently with the rest of the dashboard. They live
   on \`body.assistant-chat-active\` rather than on \`.assistant-chat\` because
   \`.content-wrapper\` is an ancestor of the chat, so the var must inherit
   downward from a common ancestor. */
body.assistant-chat-active {
  --ac-page-bg: #f2edf3;
}

.dark-mode body.assistant-chat-active,
html.dark-mode body.assistant-chat-active {
  --ac-page-bg: #151519;
}

body.assistant-chat-active {
  overflow: hidden;
}
body.assistant-chat-active .content-wrapper > [data-react-class]:not([data-react-class=BackToTopButton]) {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
body.assistant-chat-active .content-wrapper {
  /* Use the full available height below the fixed navbar, with no padding,
     so the chat reaches the viewport edges. */
  padding: 0 !important;
  min-height: 0 !important;
  /* 100vh is often taller than the *visible* viewport on mobile browsers; 100dvh
     tracks the dynamic visual viewport so the docked composer stays on-screen. */
  height: calc(100vh - 70px);
  height: calc(100dvh - 70px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--ac-page-bg) !important;
}
body.assistant-chat-active .main-panel {
  min-height: calc(100vh - 70px) !important;
  min-height: calc(100dvh - 70px) !important;
  height: calc(100vh - 70px);
  height: calc(100dvh - 70px);
  background: var(--ac-page-bg);
}
body.assistant-chat-active .footer {
  display: none !important;
}
body.assistant-chat-active .assistant-chat-page {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  background: var(--ac-page-bg);
  /* Generous side padding so the chat panel feels like a card framed by
     the page background, giving the layout breathing room and visual
     contrast (matches the dashboard cards in the rest of the app). */
  padding: 16px 24px 24px;
}
body.assistant-chat-active .assistant-chat {
  /* Card-like chat panel — clear border, rounded corners, and a soft
     shadow to lift it from the page background. */
  border: 1px solid var(--ac-border);
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.05);
}

.dark-mode body.assistant-chat-active .assistant-chat,
html.dark-mode body.assistant-chat-active .assistant-chat {
  /* Slightly stronger shadow + a hairline outline in dark mode so the chat
     panel reads as a clearly elevated card on the near-black page bg. */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4), 0 12px 32px rgba(0, 0, 0, 0.35);
}

/* Focus mode — full viewport chat; global navbar + app sidebar hidden.
   Toggled by AssistantChat via body.assistant-chat-focus-mode. */
body.assistant-chat-active.assistant-chat-focus-mode nav.navbar.default-layout-navbar {
  display: none !important;
}
body.assistant-chat-active.assistant-chat-focus-mode .navbar.fixed-top + .page-body-wrapper {
  padding-top: 0 !important;
}
body.assistant-chat-active.assistant-chat-focus-mode .page-body-wrapper {
  min-height: 100vh !important;
  min-height: 100dvh !important;
}
body.assistant-chat-active.assistant-chat-focus-mode .content-wrapper {
  height: 100vh !important;
  height: 100dvh !important;
  min-height: 100vh !important;
  min-height: 100dvh !important;
}
body.assistant-chat-active.assistant-chat-focus-mode .main-panel {
  min-height: 100vh !important;
  min-height: 100dvh !important;
  height: 100vh !important;
  height: 100dvh !important;
  width: 100% !important;
  margin-left: 0 !important;
}
@media (min-width: 992px) {
  body.assistant-chat-active.assistant-chat-focus-mode .sidebar {
    width: 0 !important;
    min-width: 0 !important;
    overflow: hidden !important;
    border: none !important;
    padding: 0 !important;
  }
  body.assistant-chat-active.assistant-chat-focus-mode .main-panel {
    width: 100% !important;
  }
}
@media (max-width: 991px) {
  body.assistant-chat-active.assistant-chat-focus-mode .main-panel {
    width: 100% !important;
    max-width: 100% !important;
  }
}
body.assistant-chat-active.assistant-chat-focus-mode .assistant-chat-page {
  padding: 0 !important;
}
body.assistant-chat-active.assistant-chat-focus-mode .assistant-chat {
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}
body.assistant-chat-active.assistant-chat-focus-mode .assistant-chat-breadcrumb {
  display: none !important;
}
body.assistant-chat-active.assistant-chat-focus-mode {
  /* Clear device notch once the fixed global navbar is hidden. */
}
body.assistant-chat-active.assistant-chat-focus-mode .assistant-chat .ac-main-header {
  padding-top: max(12px, env(safe-area-inset-top, 0px));
}

.dark-mode body.assistant-chat-active.assistant-chat-focus-mode .assistant-chat,
html.dark-mode body.assistant-chat-active.assistant-chat-focus-mode .assistant-chat {
  box-shadow: none !important;
}

.dark-mode .assistant-chat .ac-composer-wrap,
html.dark-mode .assistant-chat .ac-composer-wrap {
  box-shadow: 0 -8px 28px rgba(0, 0, 0, 0.35);
}

/* Breadcrumb above the chat panel — make sure it's readable on the page bg
   in both modes. */
.assistant-chat-breadcrumb {
  margin-bottom: 8px;
}
.assistant-chat-breadcrumb .breadcrumb {
  background: transparent;
  padding: 0;
  margin: 0;
  font-size: 0.85rem;
}
.assistant-chat-breadcrumb .breadcrumb-item {
  color: var(--ac-text-muted, #6b7280);
}
.assistant-chat-breadcrumb .breadcrumb-item.active {
  color: var(--ac-text, #14161b);
}
.assistant-chat-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
  color: var(--ac-text-faint, #9aa0aa);
}
.assistant-chat-breadcrumb a {
  color: #b66dff;
  text-decoration: none;
}
.assistant-chat-breadcrumb a:hover {
  text-decoration: underline;
}

/* On smaller viewports inside fullscreen mode, drop padding/borders entirely
   so every pixel is usable. Breadcrumb stays hidden only in focus mode
   (see body.assistant-chat-focus-mode); when the shell is visible it can
   show again on mobile. */
@media (max-width: 991px) {
  body.assistant-chat-active .assistant-chat-page {
    padding: 0;
  }
  body.assistant-chat-active .assistant-chat {
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
}
/* Examples dialog — MUI portals to document.body (outside \`.assistant-chat\`),
   so all content styles must live here with local CSS variables. */
.ac-examples-dialog {
  --ac-radius-sm: 8px;
  --ac-radius-pill: 999px;
  --ac-bg: #ffffff;
  --ac-surface: #ffffff;
  --ac-surface-2: #f3f0f6;
  --ac-surface-hover: #ebe4ee;
  --ac-border: #e8e0ec;
  --ac-border-strong: #d6cdd9;
  --ac-text: #14161b;
  --ac-text-muted: #6b7280;
  --ac-accent: #b66dff;
  --ac-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.05);
  --ac-shadow-md: 0 8px 28px rgba(15, 23, 42, 0.1);
}
.ac-examples-dialog .MuiDialog-paper {
  border-radius: 18px;
  background: var(--ac-bg);
  color: var(--ac-text);
  border: 1px solid var(--ac-border);
  box-shadow: var(--ac-shadow-md);
  overflow: hidden;
}
.ac-examples-dialog .MuiDialogTitle-root {
  margin: 0;
  padding: 18px 20px 14px;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  border-bottom: 1px solid var(--ac-border);
  background: linear-gradient(180deg, var(--ac-surface) 0%, var(--ac-bg) 100%);
}
.ac-examples-dialog .MuiDialogContent-root {
  padding: 18px 20px 22px;
}
.ac-examples-dialog .ac-examples-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px;
  border-radius: var(--ac-radius-pill);
  background: var(--ac-surface-2);
  margin-bottom: 16px;
  overflow-x: auto;
  scrollbar-width: none;
}
.ac-examples-dialog .ac-examples-tabs::-webkit-scrollbar {
  display: none;
}
.ac-examples-dialog .ac-examples-tab {
  border: none;
  background: transparent;
  color: var(--ac-text-muted);
  padding: 8px 14px;
  border-radius: var(--ac-radius-pill);
  font-size: 0.8rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.ac-examples-dialog .ac-examples-tab .MuiSvgIcon-root {
  font-size: 1.1rem;
  opacity: 0.85;
}
.ac-examples-dialog .ac-examples-tab.active {
  background: var(--ac-bg);
  color: var(--ac-text);
  box-shadow: var(--ac-shadow-sm);
}
.ac-examples-dialog .ac-examples-tab:hover:not(.active) {
  color: var(--ac-text);
  background: rgba(255, 255, 255, 0.55);
}
.ac-examples-dialog .ac-examples-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ac-examples-dialog .ac-example-item {
  text-align: left;
  width: 100%;
  background: var(--ac-surface-2);
  border: none;
  border-radius: 12px;
  padding: 14px 16px;
  color: var(--ac-text);
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.45;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
}
.ac-examples-dialog .ac-example-item:hover {
  background: var(--ac-surface-hover);
  box-shadow: var(--ac-shadow-sm);
}
.ac-examples-dialog .ac-example-item:active {
  transform: scale(0.992);
}

/* ---------------- Dark mode ----------------
   Mirrors the legacy palette ($content-bg #151519, $card-bg-varient #222328,
   $border-color #322f2f, primary #b66dff) so the chat reads identically to
   the rest of the app while still matching the new component layout. */
.dark-mode .assistant-chat,
html.dark-mode .assistant-chat {
  --ac-bg: #1a191d; /* chat-panel canvas — slightly lifted from page */
  --ac-surface: #222328; /* composer surface — legacy $card-bg-varient */
  --ac-surface-2: #1d1c20; /* code blocks / hover tints (slightly darker than canvas) */
  --ac-surface-hover: #2a2a30;
  --ac-sidebar-bg: #222328; /* lifted sidebar column above #1a191d canvas */
  --ac-border: #322f2f; /* legacy $border-color */
  --ac-border-strong: #3d3c43;
  --ac-text: #f1f1f4;
  --ac-text-muted: #a8aab2;
  --ac-text-faint: #6e7079;
  --ac-accent: #b66dff;
  --ac-accent-soft: rgba(182, 109, 255, 0.18);
  --ac-accent-text: #ffffff;
  /* Assistant message sits as an elevated card on top of the chat-panel canvas. */
  --ac-msg-assistant-bg: #222328;
  --ac-msg-assistant-border: #2a2930;
  --ac-msg-user-bg: #b66dff;
  --ac-msg-user-text: #ffffff;
  --ac-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.18);
  --ac-shadow-md: 0 6px 24px rgba(0, 0, 0, 0.30);
  --ac-shadow-lg: 0 14px 48px rgba(0, 0, 0, 0.45);
}

.dark-mode .ac-examples-dialog,
html.dark-mode .ac-examples-dialog {
  --ac-bg: #222328;
  --ac-surface: #1e1d22;
  --ac-surface-2: #2a2930;
  --ac-surface-hover: #323137;
  --ac-border: #3d3c43;
  --ac-border-strong: #4a484f;
  --ac-text: #f1f1f4;
  --ac-text-muted: #a8aab2;
  --ac-accent: #b66dff;
  --ac-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.25);
  --ac-shadow-md: 0 12px 40px rgba(0, 0, 0, 0.45);
}
.dark-mode .ac-examples-dialog .MuiDialogTitle-root,
html.dark-mode .ac-examples-dialog .MuiDialogTitle-root {
  background: linear-gradient(180deg, #25242a 0%, var(--ac-bg) 100%);
}
.dark-mode .ac-examples-dialog .ac-examples-tab:hover:not(.active),
html.dark-mode .ac-examples-dialog .ac-examples-tab:hover:not(.active) {
  background: rgba(255, 255, 255, 0.06);
}
`;
